import { useState } from 'react';
import { FileText, AlertCircle, Loader2, ArrowRight, X, Image, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileDropzone } from '@/components/ui/file-dropzone';
import type { WebSocketStatus, ExtractionResult } from '@/types/pricing';

interface UploadStepProps {
  wsStatus: WebSocketStatus;
  onExtract: (file: File) => Promise<ExtractionResult>;
  onExtractionComplete: (result: ExtractionResult) => void;
  onSkip: () => void;
}

export function UploadStep({
  wsStatus,
  onExtract,
  onExtractionComplete,
  onSkip,
}: UploadStepProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isConnected = wsStatus === 'connected';

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-8 w-8 text-blue-500" />;
    }
    if (file.type === 'application/pdf') {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    return <File className="h-8 w-8 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFilesAccepted = (files: File[]) => {
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setError(null);
    }
  };

  const handleExtract = async () => {
    if (!selectedFile) return;

    setExtracting(true);
    setError(null);
    setRetryMessage(null);

    try {
      const result = await onExtract(selectedFile);
      onExtractionComplete(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed. Please try again.');
      setExtracting(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    setRetryMessage(null);
  };

  const getStatusBadge = () => {
    switch (wsStatus) {
      case 'connected':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Connected</Badge>;
      case 'connecting':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Connecting...</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Error</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200">Disconnected</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with status inline */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Upload Booking Confirmation</h2>
          <p className="text-sm text-slate-500">PDF, PNG, JPEG, or WebP (max 32MB)</p>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge()}
        </div>
      </div>

      {/* Upload Area - no nested card */}
      <div className="relative">
          {/* Show dropzone or file preview */}
          {!selectedFile ? (
            <FileDropzone
              onFilesAccepted={handleFilesAccepted}
              accept={{
                'application/pdf': ['.pdf'],
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/webp': ['.webp'],
              }}
              maxSize={32 * 1024 * 1024}
              maxFiles={1}
              disabled={extracting}
            >
              <p className="text-sm font-medium text-gray-700">
                Drag & drop your booking confirmation here
              </p>
              <p className="text-xs text-gray-500 mt-1">
                or click to select a file
              </p>
            </FileDropzone>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getFileIcon(selectedFile)}
                  <div>
                    <p className="font-medium text-gray-900 truncate max-w-[300px]">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveFile}
                  disabled={extracting}
                  className="h-8 w-8 text-gray-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Full-card loading overlay during extraction */}
          {extracting && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg z-10">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-yellow-100 animate-ping opacity-75" style={{ animationDuration: '1.5s' }} />
                <Loader2 className="relative h-12 w-12 animate-spin text-yellow-500" />
              </div>
              <p className="mt-6 text-lg font-medium text-gray-900">Extracting booking details...</p>
              <p className="mt-1 text-sm text-gray-500">This may take a few seconds</p>
              {retryMessage && (
                <p className="mt-3 text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                  {retryMessage}
                </p>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          disabled={extracting}
          className="text-slate-500"
        >
          Skip, enter manually
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>

        <Button
          onClick={handleExtract}
          disabled={!selectedFile || !isConnected || extracting}
        >
          {extracting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Extracting...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Extract Details
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
